import React, { memo, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { CheckCircle, Eye, User, X } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Select } from '@/components/ui/Select';
import { safeArray, safeString } from '@/components/safeRender';
import { formatCurrency } from '@/lib/formatters';
import { gestorBadgeAccepted, gestorBadgeDenied } from '@/lib/uiClasses';
import {
  calcularDiasAtraso,
  getCompanyNameFromCard,
  getGestorActionStatus,
  getOperadoresFromTitulos,
  getStatusLocacaoFromTitulos,
  getValorVencidoFromTitulos,
  ordenarKanbanCards,
  type KanbanSortBy,
  type KanbanSortOrder,
} from './kanbanCardUtils';

const ROW_HEIGHT = 64;
const GRID_COLS =
  'grid-cols-[2.25rem_minmax(10rem,1.4fr)_minmax(7rem,0.9fr)_minmax(6rem,0.8fr)_minmax(6rem,0.8fr)_4.5rem_minmax(5.5rem,0.7fr)_4rem_4.5rem_5rem_minmax(9rem,0.9fr)]';

export type KanbanListEtapa = { id: string; nome: string; cor?: string };

export type KanbanListRow = {
  id: string;
  card: any;
  etapaId: string;
  etapaNome: string;
  etapaCor: string;
};

export interface KanbanCardsListProps {
  etapas: KanbanListEtapa[];
  cardsByEtapa: Record<string, any[]>;
  selectedCards: Set<string>;
  sortBy: KanbanSortBy;
  sortOrder: KanbanSortOrder;
  onCardSelect: (cardId: string, selected: boolean) => void;
  onCardClick: (card: any) => void;
  onMoveEtapa: (cardId: string, newEtapaId: string) => Promise<void>;
  movingCardId?: string | null;
}

function buildRows(
  etapas: KanbanListEtapa[],
  cardsByEtapa: Record<string, any[]>,
  etapaFilter: string
): KanbanListRow[] {
  const rows: KanbanListRow[] = [];
  for (const etapa of etapas) {
    if (etapaFilter && etapa.id !== etapaFilter) continue;
    const cards = safeArray(cardsByEtapa[etapa.id]);
    for (const card of cards) {
      if (!card?.id) continue;
      rows.push({
        id: card.id,
        card,
        etapaId: etapa.id,
        etapaNome: etapa.nome,
        etapaCor: etapa.cor || '#64748b',
      });
    }
  }
  return rows;
}

const KanbanCardsListInner: React.FC<KanbanCardsListProps> = ({
  etapas,
  cardsByEtapa,
  selectedCards,
  sortBy,
  sortOrder,
  onCardSelect,
  onCardClick,
  onMoveEtapa,
  movingCardId = null,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [etapaFilter, setEtapaFilter] = useState('');
  const [movingId, setMovingId] = useState<string | null>(null);

  const rows = useMemo(() => {
    const flat = buildRows(etapas, cardsByEtapa, etapaFilter);
    const sortedCards = ordenarKanbanCards(
      flat.map((r) => r.card),
      sortBy,
      sortOrder
    );
    const byId = new Map(flat.map((r) => [r.id, r]));
    return sortedCards
      .map((card) => byId.get(card.id))
      .filter((r): r is KanbanListRow => Boolean(r));
  }, [etapas, cardsByEtapa, etapaFilter, sortBy, sortOrder]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 8,
  });

  const busyId = movingCardId ?? movingId;

  const handleMove = async (cardId: string, newEtapaId: string, currentEtapaId: string) => {
    if (!newEtapaId || newEtapaId === currentEtapaId) return;
    try {
      setMovingId(cardId);
      await onMoveEtapa(cardId, newEtapaId);
    } finally {
      setMovingId(null);
    }
  };

  const allVisibleSelected =
    rows.length > 0 && rows.every((r) => selectedCards.has(r.id));

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="flex flex-shrink-0 flex-wrap items-center gap-2 border-b border-border px-4 py-2">
        <span className="text-xs text-muted-foreground">
          {rows.length} cliente{rows.length === 1 ? '' : 's'}
        </span>
        <Select
          value={etapaFilter}
          onValueChange={setEtapaFilter}
          className="h-8 w-auto min-w-[10rem] text-xs"
          aria-label="Filtrar por etapa"
        >
          <option value="">Todas as etapas</option>
          {etapas.map((e) => (
            <option key={e.id} value={e.id}>
              {e.nome}
            </option>
          ))}
        </Select>
      </div>

      <div
        className={`sticky top-0 z-10 hidden border-b border-border bg-muted/80 px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground backdrop-blur sm:grid sm:gap-2 ${GRID_COLS}`}
      >
        <div className="flex items-center justify-center">
          <Checkbox
            checked={allVisibleSelected}
            onChange={() => {
              const next = !allVisibleSelected;
              rows.forEach((r) => onCardSelect(r.id, next));
            }}
            aria-label="Selecionar todos visíveis"
          />
        </div>
        <div>Cliente</div>
        <div>Etapa</div>
        <div>Operador</div>
        <div>Empresa</div>
        <div className="text-right">Atraso</div>
        <div className="text-right">Valor venc.</div>
        <div className="text-right">Dias</div>
        <div>Status</div>
        <div>Gestor</div>
        <div>Ações</div>
      </div>

      {rows.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center text-muted-foreground">
          <User className="h-10 w-10 opacity-40" />
          <p className="text-sm font-medium">Nenhum cliente</p>
          <p className="text-xs">Ajuste filtros ou a busca para ver resultados.</p>
        </div>
      ) : (
        <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto">
          <div
            className="relative w-full"
            style={{ height: `${virtualizer.getTotalSize()}px` }}
          >
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const row = rows[virtualRow.index];
              if (!row) return null;
              const { card, etapaId, etapaNome, etapaCor } = row;
              const titulos = safeArray(card.titulos) as any[];
              const cliente = card.cliente;
              const gestores = getGestorActionStatus(card);
              const operadores = getOperadoresFromTitulos(titulos);
              const companyName = getCompanyNameFromCard(card);
              const statusLocacao = getStatusLocacaoFromTitulos(titulos);
              const diasAtraso = calcularDiasAtraso(titulos);
              const valorVencido = getValorVencidoFromTitulos(titulos);
              const emAtraso = titulos.filter(
                (t: any) =>
                  t.status === 'PARCIAL' ||
                  String(t.status || '').toUpperCase() === 'VENCIDO'
              ).length;
              const isSelected = selectedCards.has(card.id);
              const isBusy = busyId === card.id;

              return (
                <div
                  key={card.id}
                  data-index={virtualRow.index}
                  ref={virtualizer.measureElement}
                  className={`absolute left-0 top-0 w-full border-b border-border px-3 py-1.5 hover:bg-muted/40 ${
                    isSelected ? 'bg-green-50/80 dark:bg-green-950/20' : ''
                  }`}
                  style={{ transform: `translateY(${virtualRow.start}px)` }}
                >
                  <div
                    className={`grid items-center gap-2 text-sm ${GRID_COLS} max-sm:grid-cols-1`}
                    onClick={() => onCardClick(card)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onCardClick(card);
                      }
                    }}
                  >
                    <div
                      className="flex items-center justify-center max-sm:justify-start"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={isSelected}
                        onChange={() => onCardSelect(card.id, !isSelected)}
                        aria-label={`Selecionar ${safeString(cliente?.nome, 'cliente')}`}
                      />
                    </div>

                    <div className="min-w-0">
                      <p className="truncate font-medium text-foreground">
                        {safeString(cliente?.nome, 'Cliente sem nome')}
                      </p>
                      <p className="truncate text-xs text-muted-foreground font-mono">
                        {cliente?.codigo ? `#${cliente.codigo}` : '—'}
                        {cliente?.placa ? ` · ${cliente.placa}` : ''}
                        {titulos.length ? ` · ${titulos.length} tít.` : ''}
                      </p>
                    </div>

                    <div className="min-w-0">
                      <span className="inline-flex max-w-full items-center gap-1.5 truncate rounded-md border border-border px-1.5 py-0.5 text-xs">
                        <span
                          className="h-2 w-2 shrink-0 rounded-full"
                          style={{ backgroundColor: etapaCor }}
                          aria-hidden
                        />
                        <span className="truncate">{etapaNome}</span>
                      </span>
                    </div>

                    <div className="truncate text-xs text-muted-foreground">
                      {operadores[0]?.name ?? 'Sem operador'}
                      {operadores.length > 1 ? ` +${operadores.length - 1}` : ''}
                    </div>

                    <div className="truncate text-xs text-muted-foreground">
                      {companyName || '—'}
                    </div>

                    <div className="text-right text-xs tabular-nums text-red-600 dark:text-red-400">
                      {emAtraso}
                    </div>

                    <div className="text-right text-xs font-medium tabular-nums">
                      {formatCurrency(valorVencido)}
                    </div>

                    <div className="text-right text-xs tabular-nums">{diasAtraso}</div>

                    <div>
                      {statusLocacao ? (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                          {statusLocacao}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </div>

                    <div>
                      {gestores === 'ACEITO' ? (
                        <Badge className={`${gestorBadgeAccepted} text-[10px] px-1.5 py-0`}>
                          <CheckCircle className="mr-0.5 h-2.5 w-2.5" />
                          Aceito
                        </Badge>
                      ) : gestores === 'NEGADO' ? (
                        <Badge className={`${gestorBadgeDenied} text-[10px] px-1.5 py-0`}>
                          <X className="mr-0.5 h-2.5 w-2.5" />
                          Negado
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </div>

                    <div
                      className="flex flex-wrap items-center gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 shrink-0 px-0"
                        title="Abrir card"
                        onClick={() => onCardClick(card)}
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <Select
                        value={etapaId}
                        disabled={isBusy || etapas.length === 0}
                        onValueChange={(v) => handleMove(card.id, v, etapaId)}
                        className="h-8 min-h-8 min-w-[8rem] flex-1 py-0 pl-2 pr-6 text-xs leading-8"
                        aria-label="Mover para etapa"
                      >
                        {etapas.map((e) => (
                          <option key={e.id} value={e.id}>
                            {e.nome}
                          </option>
                        ))}
                      </Select>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export const KanbanCardsList = memo(KanbanCardsListInner);
